import type { NappletMessage } from '@napplet/core';
import { ALL_CAPABILITIES } from '@kehto/acl/capabilities';
import type { AclStateContainer } from './acl-state.js';
import type { SessionRegistry } from './session-registry.js';
import type { RuntimeAdapter, ServiceInfo, ServiceRegistry } from './types.js';
import type { createNapEnforceGate } from './enforce.js';

interface ExtensionContext {
  hooks: RuntimeAdapter;
  services: ServiceRegistry;
  registrations: Map<string, ServiceInfo>;
  sessions: SessionRegistry;
  acl: AclStateContainer;
  enforce: ReturnType<typeof createNapEnforceGate>;
  firewall: (windowId: string, message: NappletMessage, capability: string | null, respond?: boolean) => 'dispatch' | 'drop';
}

/** Internal route for host-defined contracts outside the built-in NAP domains. */
export function createServiceExtensionRouter(context: ExtensionContext) {
  const { hooks, services, registrations, sessions, acl, enforce, firewall } = context;
  const windowLifetimes = new Map<string, object>();

  function handleMessage(windowId: string, message: NappletMessage): void {
    const dot = message.type.indexOf('.');
    const domain = message.type.slice(0, dot);
    if (dot <= 0 || dot === message.type.length - 1 || !/^[a-z][a-z0-9-]*$/.test(domain)
      || !Object.hasOwn(services, domain)) return;
    const handler = services[domain];
    const policies = handler.messageCapabilities;
    if (!policies || !Object.hasOwn(policies, message.type)) return;
    const capability = policies[message.type];
    // Undefined, inherited values, and noncanonical capability strings never
    // mean permission-free. Only an explicit null opts out of the extra ACL.
    if (capability !== null && !ALL_CAPABILITIES.includes(capability)) return;
    const session = sessions.getEntryByWindowId(windowId);
    const registration = registrations.get(domain);
    if (!session || !registration) return;
    const allowed = () => hooks.isDomainAllowed?.(windowId, domain) === true
      && !acl.isBlocked('', session.dTag, session.aggregateHash)
      && (capability === null || enforce(windowId, capability, message).allowed);
    if (!allowed() || firewall(windowId, message, capability, false) === 'drop') return;

    let lifetime = windowLifetimes.get(windowId);
    if (!lifetime) {
      lifetime = {};
      windowLifetimes.set(windowId, lifetime);
    }
    handler.handleMessage(windowId, message, (response) => {
      if (registrations.get(domain) !== registration || services[domain] !== handler
        || windowLifetimes.get(windowId) !== lifetime
        || sessions.getEntryByWindowId(windowId) !== session) return;
      if (!response || typeof response.type !== 'string'
        || !response.type.startsWith(`${domain}.`) || response.type.length <= dot + 1) return;
      if (allowed()) hooks.sendToNapplet(windowId, response);
    });
  }

  return {
    handleMessage,
    destroyWindow(windowId: string): void { windowLifetimes.delete(windowId); },
    destroy(): void { windowLifetimes.clear(); },
  };
}
